import { TeamView, TeamMemberView } from '@trackly/shared';

// ── Pure transform helpers consumed only by teamSlice ──────────────

export interface TeamState {
  currentTeam: TeamView | null;
  selectedTeamId: string | null;
  isEdited: boolean;
  editedFields: string[];
  filters: {
    status: string | null;
    searchTerm: string;
  };
}

export const addEditedField = (state: TeamState, fieldName: string) => {
  if (!state.editedFields.includes(fieldName)) {
    state.editedFields.push(fieldName);
  }
  state.isEdited = true;
};

export const applyTeamUpdate = (state: TeamState, updates: Partial<TeamView>) => {
  if (!state.currentTeam) return;
  state.currentTeam = { ...state.currentTeam, ...updates };
  Object.keys(updates).forEach(field => addEditedField(state, field));
};

export const applyTeamInfoUpdate = (
  state: TeamState,
  updates: { name?: string; description?: string; status?: string },
) => {
  if (!state.currentTeam) return;
  if (updates.name !== undefined) state.currentTeam.name = updates.name;
  if (updates.description !== undefined) state.currentTeam.description = updates.description;
  if (updates.status !== undefined) state.currentTeam.status = updates.status;
  addEditedField(state, 'teamInfo');
};

export const updateComputedMemberFields = (state: TeamState) => {
  if (!state.currentTeam) return;
  state.currentTeam.memberCount = state.currentTeam.members.length;
  state.currentTeam.activeMemberCount = state.currentTeam.members.filter(m => m.isActive).length;
  state.currentTeam.memberNames = state.currentTeam.members.map(m => m.displayName);
};

export const applyMemberAdd = (
  state: TeamState,
  member: Omit<TeamMemberView, 'joinedDate'>,
) => {
  if (!state.currentTeam) return;
  const newMember: TeamMemberView = {
    ...member,
    joinedDate: new Date().toISOString(),
  };
  state.currentTeam.members.push(newMember);
  updateComputedMemberFields(state);
  addEditedField(state, 'members');
};

export const applyMemberUpdate = (
  state: TeamState,
  userId: string,
  updates: Partial<TeamMemberView>,
) => {
  if (!state.currentTeam) return;
  const idx = state.currentTeam.members.findIndex(m => m.userId === userId);
  if (idx !== -1) {
    state.currentTeam.members[idx] = { ...state.currentTeam.members[idx], ...updates };
    updateComputedMemberFields(state);
    addEditedField(state, 'members');
  }
};

export const applyMemberRemove = (state: TeamState, userId: string) => {
  if (!state.currentTeam) return;
  state.currentTeam.members = state.currentTeam.members.filter(m => m.userId !== userId);
  updateComputedMemberFields(state);
  addEditedField(state, 'members');
};

export const applyCategoryUpdate = (state: TeamState, categories: string[]) => {
  if (!state.currentTeam) return;
  state.currentTeam.assignedCategories = categories;
  state.currentTeam.categoryCount = categories.length;
  state.currentTeam.categoryValues = [...categories];
  addEditedField(state, 'assignedCategories');
};

export const resetEditState = (state: TeamState) => {
  state.isEdited = false;
  state.editedFields = [];
};

export const clearTeamState = (state: TeamState) => {
  state.currentTeam = null;
  state.selectedTeamId = null;
  state.isEdited = false;
  state.editedFields = [];
};
